import DownloadIcon from "@mui/icons-material/Download";
import { Box, CircularProgress, IconButton, Skeleton, Tooltip } from "@mui/material";
import { Pagination } from "pepsico-ds";
import PropTypes from "prop-types";
import { formatDate, truncateFilename } from "../utils/fileManagerHelpers";
import FileManagerEmptyState from "./FileManagerEmptyState";
import FileStatusBadge from "./FileStatusBadge";

const FileManagerTable = ({
  files,
  loading,
  onDownload,
  page,
  totalPages,
  onPageChange,
  operationType,
  loadingFileId,
  programTimezone,
}) => {
  // Show loading skeleton
  if (loading && files.length === 0) {
    return (
      <Box sx={{ width: "100%" }}>
        {[...Array(10)].map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            height={60}
            sx={{ marginBottom: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  // Show empty state
  if (!loading && files.length === 0) {
    return <FileManagerEmptyState operationType={operationType} />;
  }

  return (
    <Box className="file-manager-table-wrapper">
      <Box className="file-manager-table-scroll-container">
        <table className="file-manager-table">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>Filename</th>
              <th style={{ width: "20%" }}>Status</th>
              <th style={{ width: "25%" }}>Created Date</th>
              <th style={{ width: "15%", textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => {
              const isCompleted = file.status === "COMPLETED";
              const isDownloading = loadingFileId === file.id;

              return (
                <tr key={file.id}>
                  <td className="filename-cell">
                    <Tooltip title={file.fileName} placement="top" arrow>
                      <span>{truncateFilename(file.name)}</span>
                    </Tooltip>
                  </td>
                  <td>
                    <FileStatusBadge
                      status={file.status}
                      errorMessage={file.errorMessage}
                    />
                  </td>
                  <td>{formatDate(file.createdAt, programTimezone)}</td>
                  <td style={{ textAlign: "center" }}>
                    {isCompleted ? (
                      <IconButton
                        onClick={() => onDownload(file.id)}
                        disabled={isDownloading}
                        size="small"
                        aria-label="Download file"
                        sx={{
                          color: "primary.main",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        {isDownloading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                    ) : (
                      <span style={{ color: "#999" }}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {totalPages > 1 && (
        <Box className="file-manager-pagination">
          <Pagination
            currentPage={page}
            onUpdate={onPageChange}
            pageCount={totalPages}
            size="medium"
            variant="number"
          />
        </Box>
      )}
    </Box>
  );
};

FileManagerTable.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      fileName: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      errorMessage: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  onDownload: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  operationType: PropTypes.oneOf(["EXPORT", "IMPORT"]).isRequired,
  loadingFileId: PropTypes.string,
  programTimezone: PropTypes.string,
};

export default FileManagerTable;
